import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone, Mail } from "lucide-react";

export const BOPPCoatingMachine = () => {
  return (
    <>
      <Helmet>
        <title>BOPP Tape Coating Machine Manufacturer in India - Hero Impex | Since 1977</title>
        <meta name="description" content="Leading BOPP tape coating machine manufacturer in India. German quality, custom solutions, 45+ years experience. Hot melt, solvent & water-based coating systems. Get quote in 24 hours!" />
        <meta name="keywords" content="BOPP tape coating machine manufacturer India, adhesive coating machine, tape manufacturing equipment, hot melt coating machine, solvent coating machine" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-amber-500">India's Trusted Manufacturer Since 1977</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                BOPP Tape Coating Machine Manufacturer in India
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Premium quality BOPP adhesive tape coating machines built to German engineering standards. Trusted by 800+ manufacturers across 20+ countries for precision coating, reliability, and superior ROI.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8">
                    Get FREE Quote in 24 Hours
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+919884012741">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: +91-9884012741
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Leading BOPP Tape Coating Machine Manufacturer Since 1977</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Hero Impex India Private Limited is India's most trusted manufacturer of BOPP (Biaxially Oriented Polypropylene) tape coating machines with over 45 years of manufacturing excellence. We specialize in designing and manufacturing high-precision adhesive coating equipment for packaging tape, masking tape, double-sided tape, and specialty adhesive tape production.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Our BOPP tape coating machines are engineered with German technology standards and manufactured at our state-of-the-art facility in Chennai, Tamil Nadu. With 5000+ machines installed globally and serving manufacturers across India, UAE, Saudi Arabia, USA, Bangladesh, and 15+ other countries, we deliver proven performance and reliability.
              </p>
            </div>

            {/* Why Choose Hero Impex */}
            <div className="mb-12 bg-amber-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Hero Impex for BOPP Tape Coating Machines?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">45+ Years Manufacturing Experience</h4>
                    <p className="text-slate-600">Established in 1977 with deep expertise in coating and converting machinery</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">German Engineering Standards</h4>
                    <p className="text-slate-600">Precision manufacturing with pattern construction and quality components</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Custom-Built Solutions</h4>
                    <p className="text-slate-600">Machines designed as per your specific width, speed, and coating requirements</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Turnkey Plant Setup</h4>
                    <p className="text-slate-600">Complete project solutions from planning to installation and commissioning</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Pan-India Service Network</h4>
                    <p className="text-slate-600">Quick after-sales support across all major cities in India</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">ISO Certified Quality</h4>
                    <p className="text-slate-600">Export quality machines with international certifications</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">BOPP Tape Coating Machine Specifications</h2>
              <Card className="border-2 border-amber-200">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3">Standard Specifications</h4>
                      <ul className="space-y-2">
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Coating Width:</span>
                          <span className="font-semibold text-slate-900">500mm - 1600mm (Custom available)</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Machine Speed:</span>
                          <span className="font-semibold text-slate-900">Up to 200 m/min</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Coating Methods:</span>
                          <span className="font-semibold text-slate-900">Hot Melt / Solvent / Water-based</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Unwind Diameter:</span>
                          <span className="font-semibold text-slate-900">Max 1000mm</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Rewind Diameter:</span>
                          <span className="font-semibold text-slate-900">Max 800mm</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Total Power:</span>
                          <span className="font-semibold text-slate-900">35-50 KW</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3">Key Features</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">PLC with HMI touchscreen control</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Automatic tension control system</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Edge Position Control (EPC)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Hot air circulation drying system</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Corona treatment system (optional)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Turret type rewinding (optional)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Online printing attachment (optional)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Lamination unit integration (optional)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Product Image Gallery */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our BOPP Coating Machines - Real Product Images</h2>
              <p className="text-lg text-slate-700 mb-6">
                Explore our high-precision BOPP tape coating machines manufactured at our Chennai facility. All images show actual Hero Impex machines installed at customer facilities worldwide.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all overflow-hidden">
                  <img 
                    src="/images/products/coating/coating-mc-1.jpg" 
                    alt="BOPP Tape Coating Machine - Hero Impex India - Front View"
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">BOPP Coating Machine - Model 1</h4>
                    <p className="text-sm text-slate-600">Complete coating line with hot melt system, drying oven, and rewinding unit. Installed capacity: 1320mm width.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all overflow-hidden">
                  <img 
                    src="/images/products/coating/coating-mc-2.jpg" 
                    alt="Adhesive Tape Coating Machine Manufacturer in India - Hero Impex"
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Advanced Coating System</h4>
                    <p className="text-sm text-slate-600">High-speed coating machine with PLC control, automatic tension control, and multi-zone drying system.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all overflow-hidden">
                  <img 
                    src="/images/products/coating/coating-mc-3.jpg" 
                    alt="BOPP Tape Manufacturing Machine India - Hero Impex Chennai"
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Compact Coating Solution</h4>
                    <p className="text-sm text-slate-600">Space-efficient design perfect for small to medium-scale tape manufacturers. German engineering quality.</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Applications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Applications & Products</h2>
              <p className="text-lg text-slate-700 mb-6">
                Our BOPP tape coating machines are versatile and can manufacture a wide range of adhesive tape products:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Packaging Tapes</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• BOPP Packing Tape (Brown/Transparent)</li>
                      <li>• Printed Packing Tape</li>
                      <li>• Colored BOPP Tape</li>
                      <li>• Heavy-duty Packaging Tape</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Specialty Tapes</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• Masking Tape</li>
                      <li>• Double-Sided Tape</li>
                      <li>• Stationery Tape</li>
                      <li>• Low-Noise Tape</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Industrial Tapes</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• Surface Protection Tape</li>
                      <li>• Foam Tape</li>
                      <li>• Label Stock</li>
                      <li>• Custom Specialty Tapes</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Industries Served */}
            <div className="mb-12 bg-slate-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Industries We Serve</h2>
              <div className="grid md:grid-cols-4 gap-4">
                {['Packaging Industry', 'E-commerce & Logistics', 'Stationery Manufacturing', 'Converting Industry', 'Export Houses', 'Tape Trading Companies', 'Manufacturing Units', 'Industrial Suppliers'].map((industry) => (
                  <div key={industry} className="bg-white rounded-lg p-4 text-center border-2 border-amber-100">
                    <span className="font-semibold text-slate-900">{industry}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Process Flow */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Manufacturing Process Flow</h2>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { step: "1", title: "Film Unwinding", desc: "BOPP film unwinding with EPC" },
                  { step: "2", title: "Corona Treatment", desc: "Surface treatment for adhesion" },
                  { step: "3", title: "Adhesive Coating", desc: "Precision coating application" },
                  { step: "4", title: "Drying & Rewinding", desc: "Hot air drying and log formation" }
                ].map((process) => (
                  <Card key={process.step} className="text-center border-2 border-amber-200">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                        {process.step}
                      </div>
                      <h4 className="font-bold text-slate-900 mb-2">{process.title}</h4>
                      <p className="text-sm text-slate-600">{process.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-10 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Start Your BOPP Tape Manufacturing Business?</h2>
              <p className="text-xl mb-6 text-amber-50">
                Get expert consultation and detailed quotation within 24 hours. Our team will help you choose the right machine configuration for your requirements.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 text-lg px-8">
                    Request FREE Quote
                  </Button>
                </Link>
                <a href="https://wa.me/919884012741?text=I need information about BOPP tape coating machines" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                    WhatsApp: +91-9884012741
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-amber-100">
                ✓ Expert Guidance • ✓ Custom Solutions • ✓ Pan-India Installation • ✓ 45+ Years Experience
              </p>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};
